import { useQuota } from '../../components/quota/useQuota';
import { platformLabel } from '../../lib/platforms';
import { NEEDS_YOU_PAGE_SIZE } from '../dashboard/needsYouItems';
import { HISTORY_FILTERS } from '../history/historyFilters';
import { useRequestHistory } from '../history/useRequestHistory';
import { useNews } from '../news/useNews';
import { useBusiness } from '../onboarding/useBusiness';

/** Exactly the history screen's Waiting chip, so the count matches the list it names. */
const WAITING_STATUSES = HISTORY_FILTERS.find((filter) => filter.key === 'waiting')?.statuses ?? [];

export interface Suggestion {
  label: string;
  /** Sent as written, or put in the composer to finish when `prefill` is set. */
  text: string;
  prefill?: boolean;
}

interface AssistantSuggestionsProps {
  businessId: string;
  onPick: (suggestion: Suggestion) => void;
  disabled?: boolean;
}

/**
 * The empty conversation's way in: one line on what the assistant does, and up
 * to four asks built from what is true right now (posts waiting, the quota, the
 * feed). Mounted only while a conversation is empty, so the reads behind it
 * cost nothing once a chat is going.
 */
export function AssistantSuggestions({
  businessId,
  onPick,
  disabled = false,
}: AssistantSuggestionsProps) {
  const { business } = useBusiness();
  const { quota } = useQuota();
  const waiting = useRequestHistory(businessId, {
    status: WAITING_STATUSES,
    pageSize: NEEDS_YOU_PAGE_SIZE,
  });
  const news = useNews(businessId);

  const suggestions: Suggestion[] = [];

  const waitingCount = waiting.requests.length;
  if (!waiting.isLoading && waitingCount > 0) {
    const count = waiting.hasMore ? `${waitingCount}+` : String(waitingCount);
    const noun = waitingCount === 1 ? 'waiting post' : 'waiting posts';
    suggestions.push({
      label: `Go through the ${count} ${noun}`,
      text: `Go through the ${count} ${noun} with me.`,
    });
  }

  suggestions.push({ label: 'Plan next week', text: 'Plan next week for me.' });

  if (quota !== null && quota.remaining === 0) {
    suggestions.push({
      label: 'When do my posts reset?',
      text: 'When do my posts reset?',
    });
  } else {
    const platform = business?.platforms[0] ? platformLabel(business.platforms[0]) : 'LinkedIn';
    suggestions.push({
      label: `Write a ${platform} post about…`,
      text: `Write a ${platform} post about `,
      prefill: true,
    });
  }

  if ((news.feed?.items.length ?? 0) > 0) {
    suggestions.push({ label: 'What’s in my feed today?', text: 'What’s in my feed today?' });
  }

  return (
    <div className="assistant-empty">
      <p className="assistant-empty__lede">
        Ask about your posts, plans or brand. I can look things up, plan your calendar and open the
        right page for you.
      </p>
      <div className="assistant-empty__chips">
        {suggestions.slice(0, 4).map((suggestion) => (
          <button
            className="chip assistant-empty__chip"
            type="button"
            key={suggestion.label}
            disabled={disabled}
            onClick={() => onPick(suggestion)}
          >
            {suggestion.label}
          </button>
        ))}
      </div>
    </div>
  );
}
